import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE, refreshSession, redirectToLogin, pollFinalizeJob } from "@/lib/api";
import { usePillars } from "@/contexts/PillarsContext";
import { useAutoDraft } from "@/hooks/useAutoDraft";
import { LessonThumbnail, VideoThumbnail, autoThumbnailFromVideo } from "@/components/admin/ThumbnailControls";
import { checkVideoDecodable, CODEC_HELP, CORRUPT_FILE_HELP, sniffVideoContainer } from "@/lib/videoCheck";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  ArrowLeft, FloppyDisk, Trash, Plus, VideoCamera, FileText, UploadSimple,
  Link as LinkIcon, Question, Warning, NotePencil, CaretUp, CaretDown, GlobeHemisphereWest, SquaresFour,
  DownloadSimple, Play, FilmStrip, PencilSimple, Copy, CircleNotch, Sparkle, MagnifyingGlass,
} from "@phosphor-icons/react";

const ASPECTS = ["16:9", "9:16", "1:1", "4:3", "21:9"];
const INTRO = "__intro__";

// Human-friendly upload speed + time-remaining formatters for the progress readout.
const fmtSpeed = (bytesPerSec) => {
  const mbps = (bytesPerSec * 8) / 1e6;
  if (mbps >= 1) return `${mbps.toFixed(1)} Mbps`;
  return `${Math.round((bytesPerSec) / 1024)} KB/s`;
};
const fmtEta = (sec) => {
  if (!isFinite(sec) || sec < 0) return "—";
  sec = Math.round(sec);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${(m % 60).toString().padStart(2, "0")}m`;
};

// Upload FormData with real upload-progress events (fetch can't report progress).
// Auto-refreshes the access token once on a 401, and retries transient failures
// (network errors, stalls, 5xx / 429) a few times with backoff. Uses a STALL detector
// (abort only if no upload progress for a while) rather than a fixed timeout, so a slow
// but steadily-progressing chunk on a weak connection is never wrongly killed.
function uploadWithProgress(url, formData, onProgress, _authRetried = false, _attempt = 0) {
  const MAX_ATTEMPTS = 5;
  const STALL_MS = 45000; // abort + retry only if no bytes move for 45s
  return new Promise((resolve, reject) => {
    const retryTransient = (why) => {
      if (_attempt + 1 < MAX_ATTEMPTS) {
        const delay = 800 * Math.pow(2, _attempt);
        setTimeout(() => {
          uploadWithProgress(url, formData, onProgress, _authRetried, _attempt + 1).then(resolve, reject);
        }, delay);
      } else {
        reject(new Error(`${why} — please check your connection and try again`));
      }
    };

    const xhr = new XMLHttpRequest();
    let stalledAbort = false;
    let stallTimer = null;
    const clearStall = () => { if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; } };
    const armStall = () => { clearStall(); stallTimer = setTimeout(() => { stalledAbort = true; xhr.abort(); }, STALL_MS); };

    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      armStall();
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      clearStall();
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
      } else if (xhr.status === 401 && !_authRetried) {
        refreshSession()
          .then(() => uploadWithProgress(url, formData, onProgress, true, _attempt).then(resolve, reject))
          .catch((e) => { redirectToLogin(); reject(e); });
      } else if (xhr.status === 429 || xhr.status >= 500 || xhr.status === 0) {
        retryTransient(`Upload interrupted (server ${xhr.status || "unreachable"})`);
      } else {
        let detail = "Upload failed";
        try { detail = JSON.parse(xhr.responseText).detail || detail; } catch { /* noop */ }
        reject(new Error(detail));
      }
    };
    xhr.onerror = () => { clearStall(); retryTransient("Network error during upload"); };
    xhr.onabort = () => { clearStall(); if (stalledAbort) retryTransient("Upload stalled"); };
    armStall();
    xhr.send(formData);
  });
}

// Chunk-upload a video file to storage and return {id, url, label, aspect_ratio}.
// Does NOT attach to a lesson — used for swap-rule (lesson version) alternate videos.
async function uploadVideoToStorage(file, label, aspect, onProgress) {
  const CHUNK = 8 * 1024 * 1024;
  const CONCURRENCY = 8;
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const chunkPct = new Array(total).fill(0);
  const report = () => {
    const sum = chunkPct.reduce((a, b) => a + b, 0);
    onProgress && onProgress(Math.min(99, Math.round(sum / total)));
  };
  const uploadOne = async (i) => {
    const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", i);
    fd.append("file", blob, file.name);
    await uploadWithProgress(`${API_BASE}/admin/uploads/chunk`, fd, (p) => { chunkPct[i] = p; report(); });
    chunkPct[i] = 100; report();
  };
  let next = 0;
  const worker = async () => { while (next < total) { const i = next++; await uploadOne(i); } };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
  onProgress && onProgress(100);
  const { data } = await api.post("/admin/uploads/finalize-video", {
    upload_id: uploadId, filename: file.name, label: label || "", aspect_ratio: aspect || "16:9",
    total_size: file.size, total_chunks: total,
  });
  return await pollFinalizeJob(data.job_id);
}


// Finalize polling lives in lib/api (pollFinalizeJob) — shared with WebsitePanel.

export default function LessonEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const courseId = params.get("course");
  const nav = useNavigate();
  const { pillars } = usePillars();

  const isNew = id === "new";
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [tsState, setTsState] = useState("");
  const [transcript, setTranscript] = useState("");
  const [trSaving, setTrSaving] = useState(false);
  const [trSaved, setTrSaved] = useState(false);
  const [trOpen, setTrOpen] = useState(false);
  const [tidying, setTidying] = useState(false);
  const [transcriptFr, setTranscriptFr] = useState("");
  const [trFrSaving, setTrFrSaving] = useState(false);
  const [trFrSaved, setTrFrSaved] = useState(false);
  const [trFrOpen, setTrFrOpen] = useState(false);
  const [trFrBusy, setTrFrBusy] = useState(false);
  const [frHash, setFrHash] = useState(null);
  const [enHashNow, setEnHashNow] = useState(null);

  // Live staleness check: hash the current English transcript the same way the backend does
  // and compare with the hash pinned when the French transcript was last saved.
  useEffect(() => {
    let on = true;
    const src = (transcript || "").trim();
    if (!src || !transcriptFr || !window.crypto?.subtle) { setEnHashNow(null); return; }
    crypto.subtle.digest("SHA-256", new TextEncoder().encode(src)).then((buf) => {
      if (!on) return;
      setEnHashNow([...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16));
    }).catch(() => {});
    return () => { on = false; };
  }, [transcript, transcriptFr]);
  const frStale = !!(transcriptFr && frHash && enHashNow && enHashNow !== frHash);

  const refreshFrHash = async () => {
    try {
      const { data } = await api.get(`/admin/lesson-assets/${asset.id}`);
      setFrHash(data.transcript_fr_hash || null);
    } catch { /* non-critical */ }
  };

  const saveTranscriptFr = async () => {
    if (!asset?.id) return;
    setTrFrSaving(true);
    try {
      await api.patch(`/admin/lesson-assets/${asset.id}`, { transcript_fr: transcriptFr });
      setTrFrSaved(true); setTimeout(() => setTrFrSaved(false), 2500);
      refreshFrHash();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setTrFrSaving(false); }
  };

  const translateTranscriptFr = async () => {
    if (!asset?.id) return;
    setTrFrBusy(true);
    try {
      const { data } = await api.get(`/learning/lesson-assets/${asset.id}/transcript-fr`, { timeout: 120000 });
      setTranscriptFr(data.transcript_fr || "");
      setTrFrOpen(true);
      refreshFrHash();
    } catch (e) {
      setErr(e.code === "ECONNABORTED"
        ? "Translation is taking longer than usual — give it a minute and tap Translate again (it resumes where it left off)."
        : formatApiError(e));
    }
    finally { setTrFrBusy(false); }
  };

  const tidyTranscript = async () => {
    if (!asset?.id || !transcript.trim()) return;
    setTidying(true);
    try {
      const { data } = await api.post(`/admin/lesson-assets/${asset.id}/tidy-transcript`, { transcript });
      if (data.transcript) setTranscript(data.transcript);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setTidying(false); }
  };

  const refreshTranscript = async (lessonId) => {
    try {
      const { data } = await api.get(`/admin/lesson-assets/${lessonId}`);
      setTranscript(data.transcript || "");
    } catch {}
  };

  const saveTranscript = async () => {
    if (!asset?.id) return;
    setTrSaving(true);
    try {
      await api.patch(`/admin/lesson-assets/${asset.id}`, { transcript });
      setTrSaved(true); setTimeout(() => setTrSaved(false), 2500);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setTrSaving(false); }
  };

  const transcribeLesson = async () => {
    const id = asset?.id;
    if (!id) { setErr("Save the lesson first, then tap Transcribe."); return; }
    setTsState("running"); setTsProg(null); setTsErr("");
    try {
      await api.post(`/admin/lesson-assets/${id}/transcribe`);
      let misses = 0;
      const poll = async () => {
        try {
          const { data } = await api.get("/admin/coach/transcribe-status");
          misses = 0;
          const st = data?.state || {};
          if (st.running) {
            setTsProg({ videoDone: st.video_done || 0, videoTotal: st.video_total || 0 });
            setTimeout(poll, 2500);
            return;
          }
          setTsProg(null);
          const errs = st.errors || [];
          setTsState(errs.length ? "error" : "done");
          setTsErr(errs.length ? errs[errs.length - 1] : "");
          if (!errs.length && asset?.id) refreshTranscript(asset.id);
          setTimeout(() => setTsState(""), errs.length ? 30000 : 8000);
        } catch {
          // One blip (or a backend restart mid-run) shouldn't wipe the bar — retry a few times.
          if (++misses < 5) { setTimeout(poll, 4000); return; }
          setTsState(""); setTsProg(null);
        }
      };
      setTimeout(poll, 2500);
    } catch (e) { setTsState(""); setErr(formatApiError(e)); }
  };
  const [tsProg, setTsProg] = useState(null);
  const [tsErr, setTsErr] = useState("");
  const [saved, setSaved] = useState(false);

  // form state
  const [form, setForm] = useState({
    title: "", pillar: INTRO, summary: "", duration_minutes: "",
    content_type: "video", text_body: "", thumbnail_url: "",
    heavy_content: false, heavy_content_description: "", admin_notes: "",
  });
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [swapRules, setSwapRules] = useState([]);
  const [langs, setLangs] = useState([]);
  const [quizOn, setQuizOn] = useState(false);
  const [quiz, setQuiz] = useState({
    questions_per_attempt: 1, pass_threshold: 70,
    questions: [{ question: "", options: ["", "", "", ""], correct_index: 0, correct_answer_explanation: "" }],
  });

  const hydrate = (a) => {
    setAsset(a);
    setForm({
      title: a.title || "", pillar: a.pillar || INTRO, summary: a.summary || "",
      duration_minutes: a.duration_minutes || 5, content_type: a.content_type || "video",
      text_body: a.text_body || "", thumbnail_url: a.thumbnail_url || "", heavy_content: !!a.heavy_content,
      heavy_content_description: a.heavy_content_description || "", admin_notes: a.admin_notes || "",
    });
    setVideos(a.videos || []);
    setTranscript(a.transcript || "");
    setTranscriptFr(a.transcript_fr || "");
    setFrHash(a.transcript_fr_hash || null);
    setDocuments(a.documents || []);
    setSwapRules(a.swap_rules || []);
    if (a.quiz && (a.quiz.questions || []).length > 0) {
      setQuizOn(true);
      setQuiz({
        questions_per_attempt: a.quiz.questions_per_attempt || 1,
        pass_threshold: a.quiz.pass_threshold || 70,
        questions: a.quiz.questions.map(q => ({
          id: q.id, question: q.question, options: [...q.options, "", "", "", ""].slice(0, Math.max(4, q.options.length)),
          correct_index: q.correct_index, correct_answer_explanation: q.correct_answer_explanation || "",
        })),
      });
    }
  };

  const load = async (lessonId = id) => {
    if (lessonId === "new") return;
    setLoading(true); setErr("");
    try { const { data } = await api.get(`/admin/lesson-assets/${lessonId}`); hydrate(data); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // Refresh ONLY the videos list after an upload — must not clobber unsaved form edits
  // (e.g. a lesson thumbnail the user set while a video was still uploading/auto-thumbnailing).
  const refreshVideos = async (lessonId = id) => {
    if (lessonId === "new") return;
    try {
      const { data } = await api.get(`/admin/lesson-assets/${lessonId}`);
      setAsset(data);
      setVideos(data.videos || []);
    } catch { /* best-effort */ }
  };
  const skipLoadForId = useRef(null);
  useEffect(() => {
    if (isNew) return;
    // Skip the reload that our own ensureAsset()->navigate triggers, so an in-flight
    // upload's uploader (progress bar, cued file) isn't unmounted by the loading spinner.
    if (skipLoadForId.current === id) { skipLoadForId.current = null; return; }
    load();
    /* eslint-disable-next-line */
  }, [id]);

  useEffect(() => {
    api.get("/admin/translations").then(r => setLangs(r.data || [])).catch(() => {});
  }, []);

  const draftKey = `saproject:lesson-draft:${isNew ? `new${courseId ? `:${courseId}` : ""}` : id}`;
  const draftValue = { form, videos, quizOn, quiz, swapRules };
  const { restored: draftRestored, reset: resetDraft } = useAutoDraft({
    key: draftKey,
    value: draftValue,
    ready: !loading,
    onRestore: (d) => {
      if (d.form) setForm(d.form);
      if (d.videos) setVideos(d.videos);
      if (typeof d.quizOn === "boolean") setQuizOn(d.quizOn);
      if (d.quiz) setQuiz(d.quiz);
      if (d.swapRules) setSwapRules(d.swapRules);
    },
  });

  const discardDraft = () => { resetDraft(); if (!isNew) load(); };

  // Abandon: discard unsaved changes/draft and return without saving.
  const cancelEdit = () => {
    resetDraft();
    nav(courseId ? `/admin?tab=courses&course=${courseId}` : "/admin");
  };

  // Auto-suggest the lesson thumbnail from the last-used one for this pillar (only when none is set).
  const [thumbAuto, setThumbAuto] = useState(false);
  const suggestedForPillar = useRef(null);
  useEffect(() => {
    if (loading || form.thumbnail_url) return;
    if (suggestedForPillar.current === form.pillar) return;
    suggestedForPillar.current = form.pillar;
    const q = form.pillar && form.pillar !== INTRO ? `?pillar=${encodeURIComponent(form.pillar)}` : "";
    api.get(`/admin/lesson-thumbnail-suggestion${q}`)
      .then((r) => {
        const url = r.data?.thumbnail_url;
        if (url) { setForm((f) => (f.thumbnail_url ? f : { ...f, thumbnail_url: url })); setThumbAuto(true); }
      })
      .catch(() => {});
  }, [form.pillar, form.thumbnail_url, loading]);

  const setLessonThumbnail = (url) => { setThumbAuto(false); setForm((f) => ({ ...f, thumbnail_url: url })); };

  const buildQuizPayload = () => {
    if (!quizOn) return null;
    const cleaned = quiz.questions.map(q => {
      const opts = q.options.filter(o => o.trim());
      if (!q.question.trim() || opts.length < 2) throw new Error("Each question needs text + at least 2 options");
      return {
        ...(q.id ? { id: q.id } : {}),  // keep stable question ids across saves (grading + FR alignment)
        question: q.question.trim(), options: opts,
        correct_index: Math.min(q.correct_index, opts.length - 1),
        correct_answer_explanation: (q.correct_answer_explanation || "").trim() || null,
      };
    });
    if (cleaned.length < Number(quiz.questions_per_attempt))
      throw new Error("Question pool must be ≥ questions served per attempt");
    return {
      questions: cleaned,
      questions_per_attempt: Number(quiz.questions_per_attempt),
      pass_threshold: Number(quiz.pass_threshold),
    };
  };

  const buildQuizPayloadSafe = () => { try { return buildQuizPayload(); } catch { return null; } };

  // Normalize the French side-by-side rule at save time: align the FR quiz to the cleaned
  // English quiz (same kept options, same correct answer), falling back to English per-field
  // so a partly-translated quiz still serves valid questions.
  const finalizeSwapRules = (quizPayload) => swapRules.map((r) => {
    if (!(r.scope === "language" && r.scope_value === "fr")) return r;
    const L = r.lesson || {};
    const frQs = (L.quiz && L.quiz.questions) || [];
    const anyFr = frQs.some(q => (q.question || "").trim() || (q.options || []).some(o => (o || "").trim()) || (q.correct_answer_explanation || "").trim());
    let frQuiz = null;
    if (quizPayload && anyFr) {
      const questions = [];
      quiz.questions.forEach((eq, i) => {
        const kept = eq.options.map((o, oi) => [o, oi]).filter(([o]) => o.trim());
        if (!eq.question.trim() || kept.length < 2) return;
        const fq = frQs[i] || {};
        questions.push({
          question: (fq.question || "").trim() || eq.question.trim(),
          options: kept.map(([o, oi]) => (((fq.options || [])[oi] || "").trim()) || o.trim()),
          correct_index: Math.min(eq.correct_index, kept.length - 1),
          correct_answer_explanation: (fq.correct_answer_explanation || "").trim() || (eq.correct_answer_explanation || "").trim() || null,
        });
      });
      frQuiz = questions.length ? { questions, questions_per_attempt: quizPayload.questions_per_attempt, pass_threshold: quizPayload.pass_threshold } : null;
    }
    return { ...r, lesson: { ...L, quiz: frQuiz, videos: L.videos || [], documents: L.documents || [], content_type: L.content_type || form.content_type, heavy_content: !!form.heavy_content, heavy_content_description: form.heavy_content_description || "" } };
  });

  // Blank duration -> video length + 30s (quiz & coin set), rounded up to whole minutes.
  const resolveDuration = async () => {
    const n = Number(form.duration_minutes);
    if (n > 0) return n;
    const vids = videos || [];
    const b = vids.find((v) => (v.url || "").startsWith("bunny://"));
    if (b) {
      try {
        const { data } = await api.get(`/learning/bunny-playback/${b.url.slice(8)}`);
        if (data.length > 0) return Math.ceil((data.length + 30) / 60);
      } catch { /* fall through */ }
    }
    const m = vids.find((v) => (v.url || "").startsWith("/api/media/"));
    if (m) {
      const secs = await new Promise((res) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => res(v.duration || 0);
        v.onerror = () => res(0);
        setTimeout(() => res(0), 10000);
        v.src = m.url;
      });
      if (secs > 0) return Math.ceil((secs + 30) / 60);
    }
    return 5;
  };

  const save = async ({ returnToMatrix = false } = {}) => {
    setErr(""); setSaving(true); setSaved(false);
    try {
      const pillar = form.pillar === INTRO ? null : form.pillar;
      let quizPayload;
      try { quizPayload = buildQuizPayload(); }
      catch (qe) { setErr(qe.message); setSaving(false); return; }

      const payload = {
        title: form.title || "Untitled lesson", pillar,
        summary: form.summary, duration_minutes: await resolveDuration(),
        content_type: form.content_type, text_body: form.text_body,
        videos, heavy_content: form.heavy_content, thumbnail_url: form.thumbnail_url,
        heavy_content_description: form.heavy_content_description,
        admin_notes: form.admin_notes, quiz: quizPayload, swap_rules: finalizeSwapRules(quizPayload),
      };

      // Create a fresh lesson (used for brand-new lessons AND to self-heal a stale/deleted one).
      const createAndSave = async () => {
        const { data } = await api.post("/admin/lesson-assets/create-blank", {
          title: payload.title, pillar,
        });
        await api.patch(`/admin/lesson-assets/${data.id}`, payload);
        if (courseId) {
          try { await api.post(`/admin/courses/${courseId}/lessons`, { lesson_asset_id: data.id }); } catch { /* maybe already added */ }
        }
        resetDraft();
        if (returnToMatrix && courseId) nav(`/admin?tab=courses&course=${courseId}`);
        else nav(`/admin/lesson/${data.id}${courseId ? `?course=${courseId}` : ""}`, { replace: true });
      };

      if (isNew) {
        await createAndSave();
        return;
      }

      try {
        await api.patch(`/admin/lesson-assets/${id}`, payload);
      } catch (e) {
        // The lesson no longer exists (deleted or stale link) — recreate it so edits aren't lost.
        if (e?.response?.status === 404) {
          await createAndSave();
          setSaved(true); setTimeout(() => setSaved(false), 2000);
          return;
        }
        throw e;
      }
      resetDraft();
      if (returnToMatrix && courseId) {
        nav(`/admin?tab=courses&course=${courseId}`);
        return;
      }
      await load();
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Ensures a saved lesson exists (for file/document uploads). On a brand-new lesson it
  // auto-creates + saves the draft, switches the editor to that lesson, and returns its id.
  const ensureAsset = async ({ force = false } = {}) => {
    if (!force && !isNew && asset?.id) return asset.id;
    const pillar = form.pillar === INTRO ? null : form.pillar;
    const { data } = await api.post("/admin/lesson-assets/create-blank", {
      title: form.title?.trim() || "Untitled lesson", pillar,
    });
    await api.patch(`/admin/lesson-assets/${data.id}`, {
      summary: form.summary, duration_minutes: await resolveDuration(),
      content_type: form.content_type, text_body: form.text_body, videos,
      thumbnail_url: form.thumbnail_url,
      heavy_content: form.heavy_content, heavy_content_description: form.heavy_content_description,
      admin_notes: form.admin_notes, swap_rules: swapRules,
    });
    if (courseId) {
      try { await api.post(`/admin/courses/${courseId}/lessons`, { lesson_asset_id: data.id }); } catch { /* maybe already added */ }
    }
    resetDraft();
    // Set the asset in memory and skip the reload our navigation would otherwise trigger,
    // so a video upload in progress keeps its uploader mounted (progress bar stays visible).
    setAsset({ id: data.id, title: form.title, pillar, videos, thumbnail_url: form.thumbnail_url });
    skipLoadForId.current = data.id;
    nav(`/admin/lesson/${data.id}${courseId ? `?course=${courseId}` : ""}`, { replace: true });
    return data.id;
  };

  // ---- Bilingual columns: French fields live in the "language: fr" swap rule ----
  const frRule = swapRules.find(r => r.scope === "language" && r.scope_value === "fr");
  const frL = frRule?.lesson || {};
  const frQ = (frL.quiz && frL.quiz.questions) || [];
  const blankFrLesson = () => ({
    title: "", summary: "", text_body: "", content_type: form.content_type || "video",
    videos: [], documents: [], quiz: null, thumbnail_url: null, duration_minutes: null,
    heavy_content: !!form.heavy_content, heavy_content_description: form.heavy_content_description || "",
  });
  const updateFr = (patch) => setSwapRules(prev => {
    const i = prev.findIndex(r => r.scope === "language" && r.scope_value === "fr");
    if (i >= 0) return prev.map((r, idx) => idx === i ? { ...r, lesson: { ...(r.lesson || {}), ...patch } } : r);
    return [...prev, { id: crypto.randomUUID(), scope: "language", scope_value: "fr", note: "Champs français",
                       lesson: { ...blankFrLesson(), ...patch } }];
  });
  // Functional-update-capable setter so VideosSection (which uses setVideos(prev => …)) works for FR videos.
  const setFrVideos = (next) => setSwapRules(prev => {
    const i = prev.findIndex(r => r.scope === "language" && r.scope_value === "fr");
    const cur = i >= 0 ? ((prev[i].lesson || {}).videos || []) : [];
    const vids = typeof next === "function" ? next(cur) : next;
    if (i >= 0) return prev.map((r, idx) => idx === i ? { ...r, lesson: { ...(r.lesson || {}), videos: vids } } : r);
    return [...prev, { id: crypto.randomUUID(), scope: "language", scope_value: "fr", note: "Champs français",
                       lesson: { ...blankFrLesson(), videos: vids } }];
  });
  const setFrQuizField = (qi, patch) => {
    const questions = quiz.questions.map((eq, i) => {
      const cur = frQ[i] || {};
      const base = {
        question: cur.question || "",
        options: eq.options.map((_, oi) => (cur.options || [])[oi] || ""),
        correct_index: eq.correct_index,
        correct_answer_explanation: cur.correct_answer_explanation || "",
      };
      return i === qi ? { ...base, ...patch } : base;
    });
    updateFr({ quiz: { questions, questions_per_attempt: Number(quiz.questions_per_attempt) || 1, pass_threshold: Number(quiz.pass_threshold) || 70 } });
  };
  const setFrOpt = (qi, oi, val) => {
    const cur = frQ[qi] || {};
    const options = quiz.questions[qi].options.map((_, j) => (j === oi ? val : ((cur.options || [])[j] || "")));
    setFrQuizField(qi, { options });
  };

  // Per-section one-tap French translation ("basics" | "text" | "quiz" | "all")
  const [trBusy, setTrBusy] = useState("");
  const [trDone, setTrDone] = useState("");
  const translateSection = async (section) => {
    setTrBusy(section); setErr(""); setTrDone("");
    try {
      const all = section === "all";
      const { data } = await api.post("/admin/lessons/translate-fr", {
        title: all || section === "basics" ? form.title || "" : "",
        summary: all || section === "basics" ? form.summary || "" : "",
        text_body: (all || section === "text") && form.content_type === "text" ? form.text_body || "" : "",
        questions: (all || section === "quiz") && quizOn ? quiz.questions.map(q => ({
          question: q.question || "", options: q.options, explanation: q.correct_answer_explanation || "",
        })) : [],
      });
      const patch = {};
      if (all || section === "basics") { patch.title = data.title || ""; patch.summary = data.summary || ""; }
      if ((all || section === "text") && form.content_type === "text") patch.text_body = data.text_body || "";
      if ((all || section === "quiz") && quizOn && Array.isArray(data.questions) && data.questions.length) {
        patch.quiz = {
          questions: quiz.questions.map((eq, i) => ({
            question: data.questions[i]?.question || "",
            options: eq.options.map((_, oi) => data.questions[i]?.options?.[oi] || ""),
            correct_index: eq.correct_index,
            correct_answer_explanation: data.questions[i]?.explanation || "",
          })),
          questions_per_attempt: Number(quiz.questions_per_attempt) || 1,
          pass_threshold: Number(quiz.pass_threshold) || 70,
        };
      }
      updateFr(patch);
      setTrDone(section); setTimeout(() => setTrDone(""), 5000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setTrBusy(""); }
  };

  // Editor zoom: a small % input, remembered on this device.
  const [zoom, setZoom] = useState(() => {
    const z = Number(localStorage.getItem("saproject:lesson-editor-zoom"));
    return z >= 40 && z <= 200 ? z : 100;
  });
  const applyZoom = (val) => {
    const z = Math.min(200, Math.max(40, Number(val) || 100));
    setZoom(z);
    localStorage.setItem("saproject:lesson-editor-zoom", String(z));
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[#000000]">Loading lesson…</div>;

  return (
    <div className="bg-grain min-h-screen">
      <div className="mx-auto px-5 py-8" style={{ maxWidth: "1500px", zoom: zoom / 100 }}>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button data-testid="lesson-editor-back" onClick={() => nav("/admin")} className="btn-ghost flex items-center gap-1.5 text-[#000000]">
            <ArrowLeft size={16} /> Back to Operations Panel
          </button>
          {courseId && (
            <button data-testid="back-to-matrix" onClick={() => nav(`/admin?tab=courses&course=${courseId}`)} className="btn-ghost flex items-center gap-1.5 text-[#000000]">
              <SquaresFour size={16} /> Back to matrix
            </button>
          )}
          <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-[#000000]" title="Editor zoom — remembered on this device">
            <MagnifyingGlass size={14} /> Zoom
            <input data-testid="editor-zoom-input" type="number" min="40" max="200" step="5"
              className="input-base text-sm" style={{ width: "72px", padding: "0.3rem 0.5rem" }}
              key={zoom} defaultValue={zoom}
              onBlur={(e) => applyZoom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { applyZoom(e.target.value); e.target.blur(); } }} />
            %
          </label>
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#000000] font-display">Lesson editor</span>
            <h1 className="text-3xl font-extrabold font-display">{isNew ? "New lesson" : (form.title || "Untitled lesson")}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {saved && <span className="text-sm text-[#1a7f37] font-semibold" data-testid="lesson-saved-indicator">Saved ✓</span>}
            {tsState === "done" && <span className="text-sm text-[#1a7f37] font-semibold" data-testid="lesson-transcribed-indicator">Transcript ready ✓</span>}
            {tsState === "error" && <span className="text-sm text-[#FF5100] max-w-md" data-testid="lesson-transcribe-error">{tsErr || "Transcription hit a snag — try again"}</span>}
            {trDone === "all" && <span className="text-sm text-[#1a7f37] font-semibold" data-testid="fr-translated-indicator">Traduit ✓ — review, then Save lesson</span>}
            <button type="button" className="btn-secondary text-sm flex items-center gap-1.5" onClick={() => translateSection("all")} disabled={!!trBusy}
              data-testid="fr-translate-all-btn" title="AI fills every French field from the English — you review and edit before saving">
              {trBusy === "all" ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
              {trBusy === "all" ? "Traduction…" : "Translate all to French"}
            </button>
            {!isNew && (
              <button data-testid="lesson-transcribe-button" className="btn-secondary flex items-center gap-1.5"
                onClick={transcribeLesson} disabled={tsState === "running" || (videos || []).length === 0}
                title={(videos || []).length === 0 ? "Upload a video first" : "Create or update this lesson's transcript (shown under the video; used by Coach and student search)"}>
                {tsState === "running" ? <CircleNotch size={16} className="animate-spin" /> : <FileText size={16} weight="bold" />}
                {tsState === "running" ? "Transcribing…" : "Transcribe lesson"}
              </button>
            )}
            <button data-testid="lesson-cancel-button" className="btn-ghost flex items-center gap-1.5 text-[#000000]" onClick={cancelEdit} disabled={saving}>
              <ArrowLeft size={16} /> Cancel
            </button>
            {courseId && (
              <button data-testid="lesson-save-return-button" className="btn-primary flex items-center gap-1.5" onClick={() => save({ returnToMatrix: true })} disabled={saving}>
                <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save & return to matrix"}
              </button>
            )}
            <button data-testid="lesson-save-button" className={courseId ? "btn-secondary flex items-center gap-1.5" : "btn-primary flex items-center gap-1.5"} onClick={() => save()} disabled={saving}>
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save lesson"}
            </button>
          </div>
        </motion.div>

        {err && <div className="text-[#FF5100] mb-4 text-sm" data-testid="lesson-editor-error">{err}</div>}

        {draftRestored && (
          <div className="mb-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-[#CCFF00]/15 border border-[#CCFF00]/40 text-sm" data-testid="draft-restored-banner">
            <span className="text-[#000000]">We restored unsaved changes from your last session. Review and click <strong>Save lesson</strong> to keep them.</span>
            <button className="btn-ghost text-xs underline whitespace-nowrap text-[#000000]" onClick={discardDraft} data-testid="discard-draft-button">Discard draft</button>
          </div>
        )}

        {/* Basics — English column | French column */}
        <div className="card mb-5">
          <div className="flex items-center gap-2 mb-3">
            <TranslateBtn onClick={() => translateSection("basics")} busy={trBusy === "basics"} done={trDone === "basics"} testid="translate-basics-btn" />
            <h2 className="text-lg font-bold font-display m-0">Basics</h2>
          </div>
          <div className="flex flex-col gap-3">
            <ColHeads />
            <div className="grid lg:grid-cols-2 gap-3">
              <input data-testid="lesson-title-input" className="input-base" placeholder="Lesson title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input data-testid="fr-title-input" className="input-base" placeholder="Titre de la leçon…" value={frL.title || ""} onChange={(e) => updateFr({ title: e.target.value })} />
            </div>
            <div className="grid lg:grid-cols-2 gap-3">
              <textarea data-testid="lesson-summary-input" rows={2} className="input-base" placeholder="Short summary (shown in catalog/library)" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
              <textarea data-testid="fr-summary-input" rows={2} className="input-base" placeholder="Résumé court…" value={frL.summary || ""} onChange={(e) => updateFr({ summary: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.12em] text-[#000000]">
                Pillar
                <select data-testid="lesson-pillar-select" className="input-base" value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })}>
                  <option value={INTRO}>Intro / Foundational (no pillar)</option>
                  {pillars.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.12em] text-[#000000]">
                Duration (minutes)
                <input data-testid="lesson-duration-input" type="number" min="1" className="input-base" placeholder="Auto: video + 30s" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
              </label>
            </div>
            <div className="flex gap-1.5">
              {[{ id: "video", label: "Video lesson", icon: VideoCamera }, { id: "text", label: "Text lesson", icon: FileText }].map(({ id: cid, label, icon: Icon }) => (
                <button key={cid} type="button" data-testid={`lesson-content-type-${cid}`} onClick={() => setForm({ ...form, content_type: cid })}
                  className="px-3 py-1.5 rounded-full text-xs font-display font-semibold flex items-center gap-1 transition-all"
                  style={{ background: form.content_type === cid ? "#CCFF00" : "#FFFFFF", color: form.content_type === cid ? "#1F2937" : "#52525B", border: "1px solid " + (form.content_type === cid ? "#CCFF00" : "#C4C8CE"), cursor: "pointer" }}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lesson thumbnail */}
        <LessonThumbnail value={form.thumbnail_url} autoSuggested={thumbAuto} onChange={setLessonThumbnail} />

        {form.content_type === "text" && (
          <div className="card mb-5">
            <div className="flex items-center gap-2 mb-3">
              <TranslateBtn onClick={() => translateSection("text")} busy={trBusy === "text"} done={trDone === "text"} testid="translate-text-btn" />
              <h2 className="text-lg font-bold font-display m-0">Lesson text</h2>
            </div>
            <ColHeads />
            <div className="grid lg:grid-cols-2 gap-3 mt-2">
              <textarea data-testid="lesson-text-body" rows={8} className="input-base" placeholder="Lesson content…" value={form.text_body} onChange={(e) => setForm({ ...form, text_body: e.target.value })} />
              <textarea data-testid="fr-text-body-input" rows={8} className="input-base" placeholder="Contenu de la leçon…" value={frL.text_body || ""} onChange={(e) => updateFr({ text_body: e.target.value })} />
            </div>
          </div>
        )}

        {form.content_type === "video" && (
          <div className="grid lg:grid-cols-2 gap-5 items-start" data-testid="bilingual-videos-grid">
            <VideosSection heading="Videos — English" assetId={asset?.id} isNew={isNew} ensureAsset={ensureAsset} videos={videos} setVideos={setVideos} onUploaded={refreshVideos}
              onTranscribe={transcribeLesson} transcribing={tsState === "running"} />
            <VideosSection heading="Vidéos — Français" standalone videos={frL.videos || []} setVideos={setFrVideos} testidPrefix="fr-"
              blurb={"Optional: upload native French video(s) here — French mode plays them instead. If left empty, French mode plays the English video with an AI French voice-over and French subtitles automatically. Tap Save lesson to keep changes."} />
          </div>
        )}

        {form.content_type === "video" && !isNew && (
          <div className="grid lg:grid-cols-2 gap-5 items-start">
          <div className="card mb-5" data-testid="lesson-transcript-section">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-lg font-bold font-display flex items-center gap-1.5 m-0">
                <FileText size={18} color="#00B8D4" /> Transcript — English{transcript ? "" : " (none yet)"}
              </h2>
              <div className="flex items-center gap-2">
                {trSaved && <span className="text-sm text-[#1a7f37] font-semibold" data-testid="transcript-saved-indicator">Transcript saved ✓</span>}
                <button className="btn-ghost text-sm" onClick={() => setTrOpen((o) => !o)} data-testid="transcript-toggle-btn">
                  {trOpen ? "Hide" : transcript ? "Read / edit" : "Open"}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#000000] mt-1 mb-0">
              Shown to students under the video, searchable, and used by Coach. New video uploads transcribe
              automatically; use "Transcribe lesson" above to redo it from the audio.
            </p>
            {tsState === "running" && (
              <div className="mt-3" data-testid="transcribe-progress">
                <div className="flex justify-between text-[11px] text-[#52525B] mb-1">
                  <span>
                    {tsProg?.videoTotal
                      ? `Transcribing video ${Math.min((tsProg.videoDone || 0) + 1, tsProg.videoTotal)} of ${tsProg.videoTotal}…`
                      : "Transcribing — downloading audio…"}
                  </span>
                  <span data-testid="transcribe-progress-percent">
                    {tsProg?.videoTotal ? `${Math.round(((tsProg.videoDone || 0) / tsProg.videoTotal) * 100)}%` : ""}
                  </span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${tsProg?.videoTotal ? Math.max(6, ((tsProg.videoDone || 0) / tsProg.videoTotal) * 100) : 6}%`, transition: "width 0.6s ease" }} />
                </div>
              </div>
            )}
            {trOpen && (
              <div className="mt-3">
                <textarea data-testid="transcript-textarea" rows={12} className="input-base font-mono text-sm"
                  placeholder='No transcript yet — upload a video (it transcribes automatically) or tap "Transcribe lesson" above.'
                  value={transcript} onChange={(e) => setTranscript(e.target.value)} />
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#52525B]">{transcript.length.toLocaleString()} characters</span>
                    <button className="btn-secondary text-sm flex items-center gap-1.5" onClick={tidyTranscript}
                      disabled={tidying || !transcript.trim()} data-testid="transcript-tidy-btn"
                      title="AI cleans punctuation, paragraphs and speech slips — you review the result before saving">
                      {tidying ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
                      {tidying ? "Tidying…" : "Tidy up transcript"}
                    </button>
                  </div>
                  <button className="btn-primary text-sm" onClick={saveTranscript} disabled={trSaving} data-testid="transcript-save-btn">
                    {trSaving ? "Saving…" : "Save transcript"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="card mb-5" data-testid="fr-lesson-transcript-section">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-lg font-bold font-display flex items-center gap-1.5 m-0">
                <FileText size={18} color="#00768a" /> Transcription — Français{transcriptFr ? "" : " (aucune)"}
              </h2>
              <div className="flex items-center gap-2">
                {trFrSaved && <span className="text-sm text-[#1a7f37] font-semibold" data-testid="fr-transcript-saved-indicator">Enregistrée ✓</span>}
                <button className="btn-ghost text-sm" onClick={() => setTrFrOpen((o) => !o)} data-testid="fr-transcript-toggle-btn">
                  {trFrOpen ? "Hide" : transcriptFr ? "Read / edit" : "Open"}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#000000] mt-1 mb-0">
              Shown to French-mode students. Use Translate to fill it from the English, then review and save.
              If left empty, students get an automatic translation.
            </p>
            {frStale && (
              <div className="mt-2 text-xs font-semibold px-3 py-2 rounded-xl flex items-start gap-1.5"
                style={{ background: "#FFF4E5", border: "1px solid #E0A800", color: "#7a5200" }}
                data-testid="fr-transcript-stale-warning">
                <span>⚠</span>
                <span>The English transcript changed after this French version was saved — tap "Translate from English" to refresh it, review, then Save.</span>
              </div>
            )}
            {trFrOpen && (
              <div className="mt-3">
                <textarea data-testid="fr-transcript-textarea" rows={12} className="input-base font-mono text-sm"
                  placeholder="Aucune transcription française — utilisez « Translate from English »."
                  value={transcriptFr} onChange={(e) => setTranscriptFr(e.target.value)} />
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#52525B]">{transcriptFr.length.toLocaleString()} characters</span>
                    <button className="btn-secondary text-sm flex items-center gap-1.5" onClick={translateTranscriptFr}
                      disabled={trFrBusy || !transcript.trim()} data-testid="fr-transcript-translate-btn"
                      title="AI translates the saved English transcript into French — review before saving">
                      {trFrBusy ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
                      {trFrBusy ? "Traduction…" : "Translate from English"}
                    </button>
                  </div>
                  <button className="btn-primary text-sm" onClick={saveTranscriptFr} disabled={trFrSaving} data-testid="fr-transcript-save-btn">
                    {trFrSaving ? "Saving…" : "Save transcript"}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-5 items-start">
          <DocumentsSection heading="Documents / PDFs — English" assetId={asset?.id} isNew={isNew} ensureAsset={ensureAsset} documents={documents} setDocuments={setDocuments} onChanged={load} />
          <DocumentsSection heading="Documents / PDF — Français" standalone testidPrefix="fr-" documents={frL.documents || []}
            setDocuments={(docs) => updateFr({ documents: docs })}
            blurb={"Optional French documents — shown in French mode instead of the English ones. If left empty, French mode shows the English documents. Tap Save lesson to keep changes."} />
        </div>

        <LessonVersionsSection swapRules={swapRules} setSwapRules={setSwapRules} langs={langs} getCurrentLesson={() => ({
          title: form.title || "Untitled version",
          summary: form.summary || "",
          content_type: form.content_type || "video",
          text_body: form.text_body || "",
          thumbnail_url: form.thumbnail_url || "",
          duration_minutes: Number(form.duration_minutes) || 5,
          videos: JSON.parse(JSON.stringify(videos || [])),
          documents: JSON.parse(JSON.stringify(documents || [])),
          quiz: buildQuizPayloadSafe(),
          heavy_content: !!form.heavy_content,
          heavy_content_description: form.heavy_content_description || "",
        })} />

        {/* Quiz — English column | French column */}
        <div className="card mb-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <TranslateBtn onClick={() => translateSection("quiz")} busy={trBusy === "quiz"} done={trDone === "quiz"} testid="translate-quiz-btn" />
              <h2 className="text-lg font-bold font-display flex items-center gap-1.5 m-0"><Question size={18} /> Quiz question pool</h2>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer text-[#000000]">
              <input type="checkbox" data-testid="lesson-quiz-toggle" checked={quizOn} onChange={(e) => setQuizOn(e.target.checked)} /> Attach quiz
            </label>
          </div>
          {quizOn && <QuizEditor quiz={quiz} setQuiz={setQuiz} frQ={frQ} setFrQuizField={setFrQuizField} setFrOpt={setFrOpt} />}
          {!quizOn && <p className="text-sm text-[#000000] italic">No quiz on this lesson.</p>}
        </div>

        {/* Heavy content */}
        <div className="card mb-5">
          <h2 className="text-lg font-bold font-display flex items-center gap-1.5 mb-3"><Warning size={18} color="#FF5100" /> Heavy content warning</h2>
          <label className="flex items-center gap-2 text-sm cursor-pointer text-[#000000] mb-2">
            <input type="checkbox" data-testid="lesson-heavy-toggle" checked={form.heavy_content} onChange={(e) => setForm({ ...form, heavy_content: e.target.checked })} />
            Flag this lesson as Heavy Content (shows a warning overlay on a student's first visit)
          </label>
          {form.heavy_content && (
            <input data-testid="lesson-heavy-desc" className="input-base" placeholder="1-line description (e.g. contains depictions of physical violence)" value={form.heavy_content_description} onChange={(e) => setForm({ ...form, heavy_content_description: e.target.value })} />
          )}
        </div>

        {/* Notes to self */}
        <div className="card mb-5">
          <h2 className="text-lg font-bold font-display flex items-center gap-1.5 mb-3"><NotePencil size={18} /> Notes to self (admin only)</h2>
          <textarea data-testid="lesson-notes-input" rows={3} className="input-base" placeholder="Private notes — never shown to students." value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
        </div>

        <div className="flex justify-end gap-2 flex-wrap">
          <button data-testid="lesson-save-button-bottom" className={courseId ? "btn-secondary flex items-center gap-1.5" : "btn-primary flex items-center gap-1.5"} onClick={() => save()} disabled={saving}>
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save lesson"}
          </button>
          {courseId && (
            <button data-testid="lesson-save-return-button-bottom" className="btn-primary flex items-center gap-1.5" onClick={() => save({ returnToMatrix: true })} disabled={saving}>
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save & return to matrix"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function isEmbedUrl(u) { return /youtube|youtu\.be|vimeo/i.test(u || ""); }

function VideoPreview({ url, aspect, poster }) {
  const [failed, setFailed] = useState(false);
  const isBunny = (url || "").startsWith("bunny://");
  const bunnyGuid = isBunny ? url.slice(8) : null;
  const [bunnyState, setBunnyState] = useState(isBunny ? "loading" : "ready");
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const attachBunny = async () => {
    const v = videoRef.current;
    if (!v || !bunnyGuid) return;
    try {
      const { data } = await api.get(`/learning/bunny-playback/${bunnyGuid}`);
      if (data.status !== 4) { setBunnyState("processing"); return; }
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      if (v.canPlayType("application/vnd.apple.mpegurl")) {
        v.src = data.hls_url;
      } else {
        const Hls = (await import("hls.js")).default;
        if (Hls.isSupported()) {
          const hls = new Hls({ enableWorker: true });
          hlsRef.current = hls;
          hls.on(Hls.Events.ERROR, (_e, d) => {
            if (!d.fatal) return;
            if (d.type === "networkError") hls.startLoad();
            else if (d.type === "mediaError") hls.recoverMediaError();
            else setFailed(true);
          });
          hls.loadSource(data.hls_url);
          hls.attachMedia(v);
        } else {
          v.src = data.mp4_url;
        }
      }
      setBunnyState("ready");
    } catch {
      setBunnyState((s) => (s === "loading" ? "processing" : s));
    }
  };
  useEffect(() => {
    if (!isBunny) return;
    attachBunny();
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bunnyGuid]);
  useEffect(() => {
    if (bunnyState !== "processing") return;
    const t = setInterval(attachBunny, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bunnyState]);

  const ratioMap = { "16:9": "16 / 9", "9:16": "9 / 16", "1:1": "1 / 1", "4:3": "4 / 3", "21:9": "21 / 9" };
  const ar = aspect || "16:9";
  const portrait = ar === "9:16" || ar === "1:1";
  return (
    <div className="bg-black rounded-lg overflow-hidden mx-auto relative"
      style={{ aspectRatio: ratioMap[ar] || "16 / 9", maxWidth: portrait ? "240px" : "100%", maxHeight: "300px", width: "100%" }}>
      {isEmbedUrl(url)
        ? <iframe src={url} title="preview" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        : failed
          ? <div className="w-full h-full grid place-items-center p-4 text-center text-xs text-white/85" data-testid="video-decode-error">{CODEC_HELP}</div>
          : (
            <>
              <video ref={videoRef} src={isBunny ? undefined : url} controls preload="metadata" poster={poster || undefined} onError={() => { if (!isBunny) setFailed(true); }} className="w-full h-full object-contain bg-black" />
              {isBunny && bunnyState !== "ready" && (
                <div className="absolute inset-0 grid place-items-center p-4 text-center text-xs text-white/85 bg-black/80" data-testid="bunny-preview-status">
                  {bunnyState === "loading" ? "Loading video…" : "Bunny is still transcoding this video — the preview will appear here automatically once it's ready."}
                </div>
              )}
            </>
          )}
    </div>
  );
}

function VideosSection({ assetId, isNew, ensureAsset, videos, setVideos, onUploaded, standalone = false, onTranscribe = null, transcribing = false, heading = null, blurb = null, testidPrefix = "" }) {
  const T = testidPrefix;
  const [tab, setTab] = useState("upload");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [uploadStats, setUploadStats] = useState(null); // { speed (B/s), etaSec }
  const [openIdx, setOpenIdx] = useState(null);
  const [libItems, setLibItems] = useState(null);
  const [codecWarn, setCodecWarn] = useState("");
  const [thumbJobs, setThumbJobs] = useState({}); // video id -> "pending" | "failed"
  const fileRef = useRef(null);
  const markThumb = (vidId, state) => setThumbJobs((m) => {
    if (state === null) { const { [vidId]: _drop, ...rest } = m; return rest; }
    return { ...m, [vidId]: state };
  });

  const acceptFile = async (f) => {
    if (!f) return;
    if (!/\.(mp4|webm|mov|m4v)$/i.test(f.name)) { setErr("Unsupported file — use mp4, webm, mov or m4v"); return; }
    if (!(await sniffVideoContainer(f))) { setErr(CORRUPT_FILE_HELP); setFile(null); return; }
    setErr(""); setCodecWarn(""); setFile(f);
    checkVideoDecodable(f).then((ok) => {
      if (!ok) setCodecWarn("Heads up: this browser can't preview this file's codec (likely HEVC/H.265 from an iPhone or camera) — but that's fine: it will be converted to web-friendly streaming formats automatically after upload, so students will see it normally.");
    });
  };

  // Persist the videos array to the backend so client-side edits (delete / reorder) can't
  // be resurrected by the next upload's server-side append + refresh.
  const persistVideos = async (next) => {
    setVideos(next); setOpenIdx(null);
    if (!standalone && assetId) {
      try { await api.patch(`/admin/lesson-assets/${assetId}`, { videos: next }); }
      catch (e) { setErr(formatApiError(e)); }
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= videos.length) return;
    const copy = [...videos];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    persistVideos(copy);
  };
  const remove = async (i) => {
    const gone = videos[i];
    await persistVideos(videos.filter((_, idx) => idx !== i));
    // Bunny-hosted videos can be truly deleted (backend refuses if another lesson uses it).
    if (gone?.url?.startsWith("bunny://")) {
      api.post("/admin/bunny/delete-video", { url: gone.url }).catch(() => {});
    }
  };
  const setField = (i, key, val) => setVideos(videos.map((v, idx) => idx === i ? { ...v, [key]: val } : v));

  const addUrl = () => {
    if (!url.trim()) { setErr("Enter a video URL"); return; }
    setErr("");
    setVideos([...videos, { id: crypto.randomUUID(), url: url.trim(), label, aspect_ratio: aspect }]);
    setUrl(""); setLabel("");
  };

  const uploadFile = async () => {
    setErr("");
    if (!file) { setErr("Drag or choose a video file first"); return; }
    // Standalone (lesson version): upload to storage without attaching to any lesson.
    if (standalone) {
      setBusy(true); setProgress(0);
      try {
        const vid = await uploadVideoToStorage(file, label, aspect, (p) => setProgress(p));
        const vidId = vid.id || crypto.randomUUID();
        setVideos([...videos, { ...vid, id: vidId, label, aspect_ratio: aspect }]);
        setFile(null); setLabel(""); setCodecWarn(""); if (fileRef.current) fileRef.current.value = "";
        // Grab a thumbnail in the background so the upload feels instant (media-store only).
        if (vid.url && !vid.url.startsWith("bunny://")) {
          markThumb(vidId, "pending");
          autoThumbnailFromVideo(vid.url)
            .then((thumb) => {
              if (!thumb) { markThumb(vidId, "failed"); return; }
              setVideos((prev) => prev.map((v) => (v.id === vidId ? { ...v, thumbnail_url: thumb } : v)));
              markThumb(vidId, null);
            })
            .catch(() => markThumb(vidId, "failed"));
        }
      } catch (e) { setErr(e.message); }
      finally { setBusy(false); setProgress(null); setUploadStats(null); }
      return;
    }
    setBusy(true); setProgress(0);
    try {
      let targetId = assetId || await ensureAsset();
      // Throughput here is limited by the network path to the origin (not chunking), so we
      // use small chunks for resilient retries + a few parallel streams (helps on HTTP/1.1
      // production paths; neutral on the HTTP/2 preview path).
      const CHUNK = 8 * 1024 * 1024;   // 8MB chunks — small retry unit on slow links
      const CONCURRENCY = 8;
      const uploadId = crypto.randomUUID();
      const total = Math.max(1, Math.ceil(file.size / CHUNK));
      const chunkPct = new Array(total).fill(0);
      const sizeOf = (i) => Math.min(file.size, (i + 1) * CHUNK) - i * CHUNK;
      const startAt = Date.now();
      // Throttle progress re-renders: on every XHR progress event across parallel chunks,
      // re-rendering the whole uploader steals main-thread time from the uploads themselves.
      // Update the bar + speed/ETA readout at most ~4x/sec (and always at 100%).
      let lastRenderedPct = -1;
      let lastRenderAt = 0;
      const reportProgress = (force = false) => {
        const sum = chunkPct.reduce((a, b) => a + b, 0);
        const pct = Math.min(99, Math.round(sum / total));
        const now = Date.now();
        if (!force && pct === lastRenderedPct) return;
        if (!force && now - lastRenderAt < 250) return;
        lastRenderedPct = pct; lastRenderAt = now;
        setProgress(pct);
        const uploadedBytes = chunkPct.reduce((acc, p, i) => acc + (p / 100) * sizeOf(i), 0);
        const elapsed = (now - startAt) / 1000;
        if (elapsed > 0.5 && uploadedBytes > 0) {
          const speed = uploadedBytes / elapsed;          // bytes/sec (average)
          const etaSec = Math.max(0, (file.size - uploadedBytes) / speed);
          setUploadStats({ speed, etaSec });
        }
      };
      const uploadOne = async (i) => {
        const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", blob, file.name);
        await uploadWithProgress(`${API_BASE}/admin/uploads/chunk`, fd, (p) => { chunkPct[i] = p; reportProgress(); });
        chunkPct[i] = 100; reportProgress(true);
      };
      let next = 0;
      const worker = async () => { while (next < total) { const i = next++; await uploadOne(i); } };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
      setProgress(100); setUploadStats(null);
      const finalizeBody = { upload_id: uploadId, filename: file.name, label, aspect_ratio: aspect,
                             total_size: file.size, total_chunks: total };
      let newVid;
      try {
        const { job_id } = await api.post(`/admin/lesson-assets/${targetId}/video/finalize`, finalizeBody).then((r) => r.data || r);
        setUploadStats({ processing: true });
        newVid = await pollFinalizeJob(job_id);
      } catch (e) {
        // The lesson vanished underneath us (deleted/stale) — the uploaded chunks live under
        // upload_id (not the lesson), so recreate the lesson and finalize onto it. No re-upload.
        if (e?.response?.status === 404) {
          targetId = await ensureAsset({ force: true });
          const { job_id } = await api.post(`/admin/lesson-assets/${targetId}/video/finalize`, finalizeBody).then((r) => r.data || r);
          setUploadStats({ processing: true });
          newVid = await pollFinalizeJob(job_id);
        } else { throw e; }
      }
      setFile(null); setLabel(""); if (fileRef.current) fileRef.current.value = "";
      await onUploaded(targetId);
      setBusy(false); setProgress(null);
      // Bunny-hosted videos generate their own thumbnail; only grab a frame for media-store URLs.
      if (newVid?.id && newVid?.url && !newVid.url.startsWith("bunny://")) {
        const vidId = newVid.id;
        markThumb(vidId, "pending");
        autoThumbnailFromVideo(newVid.url)
          .then(async (thumb) => {
            if (!thumb) { markThumb(vidId, "failed"); return; }
            const { data: fresh } = await api.get(`/admin/lesson-assets/${targetId}`);
            const vids = (fresh.videos || []).map((v) => (v.id === vidId ? { ...v, thumbnail_url: thumb } : v));
            await api.patch(`/admin/lesson-assets/${targetId}`, { videos: vids });
            await onUploaded(targetId);
            markThumb(vidId, null);
          })
          .catch(() => markThumb(vidId, "failed"));
      }
      return;
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); setProgress(null); setUploadStats(null); }
  };

  const loadLibrary = async () => {
    setTab("library"); setErr("");
    if (libItems) return;
    try {
      const { data } = await api.get("/admin/lesson-assets");
      const seen = new Set(); const items = [];
      data.forEach((a) => {
        (a.videos || []).forEach((v) => {
          if (v.url && !seen.has(v.url)) { seen.add(v.url); items.push({ url: v.url, label: v.label, aspect_ratio: v.aspect_ratio, from: a.title }); }
        });
        if (a.video_url && !seen.has(a.video_url)) { seen.add(a.video_url); items.push({ url: a.video_url, label: "", aspect_ratio: "16:9", from: a.title }); }
      });
      setLibItems(items);
    } catch (e) { setErr(formatApiError(e)); }
  };
  const addFromLibrary = (item) => {
    if (videos.some((v) => v.url === item.url)) { setErr("That video is already in this lesson"); return; }
    setErr("");
    setVideos([...videos, { id: crypto.randomUUID(), url: item.url, label: item.label || "", aspect_ratio: item.aspect_ratio || "16:9" }]);
  };

  const TABS = [
    { id: "upload", label: "Upload from device", icon: UploadSimple },
    { id: "library", label: "From Library", icon: FilmStrip },
    { id: "link", label: "Link / Embed", icon: LinkIcon },
  ];

  return (
    <div className="card mb-5" data-testid={`${T}lesson-videos-section`}>
      <h2 className="text-lg font-bold font-display flex items-center gap-1.5 mb-1"><VideoCamera size={18} color="#00B8D4" /> {heading || "Videos"} ({videos.length})</h2>
      <p className="text-xs text-[#000000] mb-3">{blurb || "Add one or more ordered videos (intro / main / outro). Click the ▶ play button on any video to watch it and verify it uploaded correctly."}</p>

      <div className="flex flex-col gap-3 mb-4">
        {videos.map((v, i) => (
          <div key={v.id || i} className="rounded-xl bg-[#FFFFFF] border border-[#C4C8CE] p-3" data-testid={`${T}lesson-video-${i}`}>
            <div className="flex items-center gap-2">
              <span className="w-5 text-center font-display font-bold text-[#000000]">{i + 1}</span>
              <input className="input-base text-xs min-w-0 placeholder:text-[#A1A1AA] placeholder:italic" style={{ flex: "1 1 auto", minWidth: "160px" }} placeholder="Optional label (e.g. Intro)" value={v.label || ""} onChange={(e) => setField(i, "label", e.target.value)} data-testid={`${T}lesson-video-${i}-label`} />
              <select className="input-base text-xs" style={{ flex: "0 0 76px", width: "76px", padding: "0.5rem 0.4rem" }} value={v.aspect_ratio || "16:9"} onChange={(e) => setField(i, "aspect_ratio", e.target.value)} data-testid={`${T}lesson-video-${i}-aspect`}>
                {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button className="btn-ghost p-1" onClick={() => setOpenIdx(openIdx === i ? null : i)} data-testid={`${T}preview-video-${i}`} title="Play / preview">
                <Play size={16} weight="fill" color={openIdx === i ? "#7a8a00" : "#00B8D4"} />
              </button>
              {!isEmbedUrl(v.url) && !(v.url || "").startsWith("bunny://") && (
                <a className="btn-ghost p-1" href={v.url} download target="_blank" rel="noreferrer" data-testid={`${T}download-video-${i}`} title="Download this video"><DownloadSimple size={16} color="#52525B" /></a>
              )}
              {(v.url || "").startsWith("bunny://") && (
                <button className="btn-ghost p-1" data-testid={`${T}download-video-${i}`} title="Download this video (720p MP4)"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/learning/bunny-playback/${v.url.slice(8)}`);
                      if (data.mp4_url) window.open(data.mp4_url, "_blank", "noopener");
                    } catch { /* noop */ }
                  }}>
                  <DownloadSimple size={16} color="#52525B" />
                </button>
              )}
              <button className="btn-ghost p-1" onClick={() => move(i, -1)} title="Move up"><CaretUp size={14} /></button>
              <button className="btn-ghost p-1" onClick={() => move(i, 1)} title="Move down"><CaretDown size={14} /></button>
              <button className="btn-ghost p-1" onClick={() => remove(i)} data-testid={`${T}remove-video-${i}`} title="Remove"><Trash size={14} color="#FF5100" /></button>
            </div>
            <div className="text-[10px] text-[#000000] truncate mt-1 pl-7">{v.url}</div>
            {onTranscribe && !standalone && (
              <div className="mt-1.5 pl-7">
                <button className="btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-[#C4C8CE] rounded-lg"
                  onClick={onTranscribe} disabled={transcribing} data-testid={`transcribe-video-${i}`}
                  title="Start the transcript over from this lesson's video audio (auto-transcribe already runs after each upload)">
                  {transcribing ? <CircleNotch size={13} className="animate-spin" /> : <FileText size={13} />}
                  {transcribing ? "Transcribing…" : "Transcribe video"}
                </button>
              </div>
            )}
            <VideoThumbnail video={v} onChange={(url) => { setField(i, "thumbnail_url", url); markThumb(v.id, null); }} />
            {thumbJobs[v.id] === "pending" && !v.thumbnail_url && (
              <div className="mt-1.5 pl-7 flex items-center gap-1.5 text-[11px] text-[#00768a]" data-testid={`video-thumb-generating-${i}`}>
                <CircleNotch size={13} className="animate-spin" /> Grabbing a thumbnail from the video… this can take a minute for large files.
              </div>
            )}
            {thumbJobs[v.id] === "failed" && !v.thumbnail_url && (
              <div className="mt-1.5 pl-7 text-[11px] text-[#b45309]" data-testid={`video-thumb-failed-${i}`}>
                Couldn't auto-grab a frame — use <strong>Capture frame</strong> above to pick one, or upload an image.
              </div>
            )}
            {openIdx === i && <div className="mt-2" data-testid={`${T}video-player-${i}`}><VideoPreview url={v.url} aspect={v.aspect_ratio} poster={v.thumbnail_url} /></div>}
          </div>
        ))}
        {videos.length === 0 && <div className="text-xs italic text-[#000000]">No videos yet — add one below.</div>}
      </div>

      <div className="p-3 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {TABS.map(({ id: m, label: l, icon: Icon }) => (
            <button key={m} type="button" data-testid={`${T}video-tab-${m}`} onClick={() => (m === "library" ? loadLibrary() : setTab(m))}
              className="px-3 py-1.5 rounded-full text-xs font-display font-semibold flex items-center gap-1 transition-all"
              style={{ background: tab === m ? "#CCFF00" : "#FFFFFF", color: tab === m ? "#1F2937" : "#52525B", border: "1px solid " + (tab === m ? "#CCFF00" : "#C4C8CE"), cursor: "pointer" }}>
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>

        {tab === "upload" && (
          <>
            <div
              data-testid={`${T}video-dropzone`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
              onDrop={(e) => { e.preventDefault(); setDragging(false); acceptFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className="rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-all"
              style={{ borderColor: dragging ? "#CCFF00" : "#C4C8CE", background: dragging ? "rgba(204,255,0,0.10)" : "#FFFFFF" }}
            >
              <UploadSimple size={26} className="mx-auto mb-1" color={dragging ? "#7a8a00" : "#A1A1AA"} />
              {file
                ? <div className="text-sm font-semibold text-[#000000] truncate" data-testid={`${T}video-selected-file`}>{file.name}</div>
                : <div className="text-sm text-[#00768a] font-semibold"><span className="underline">Click to upload</span> <span className="text-[#000000] font-normal">or drag and drop</span></div>}
              <div className="text-[11px] text-[#000000] mt-1">Supported: .mp4, .webm, .mov, .m4v · Recommended 16:9 · large files may take a while</div>
              <input ref={fileRef} data-testid={`${T}video-file-input`} type="file" accept="video/mp4,video/webm,video/quicktime,.m4v" className="hidden" onChange={(e) => acceptFile(e.target.files?.[0] || null)} />
            </div>
            {codecWarn && (
              <div className="rounded-xl border border-amber-400 bg-amber-50 text-amber-900 text-xs px-3 py-2 leading-relaxed" data-testid="codec-warning">
                <Warning size={14} weight="fill" className="inline mr-1 -mt-0.5" color="#b45309" /> {codecWarn}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input className="input-base text-sm" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`${T}video-label-input`} />
              <select className="input-base text-sm" value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid={`${T}video-aspect-input`}>
                {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            {busy && progress !== null && (
              <div className="flex flex-col gap-1" data-testid={`${T}video-upload-progress`}>
                <div className="flex justify-between text-[11px] text-[#000000]">
                  <span>{progress < 100 ? "Uploading…" : "Finishing up…"}</span>
                  <span data-testid={`${T}video-upload-percent`}>{progress}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[#ECEDEF] overflow-hidden">
                  <div className="h-full bg-[#CCFF00] transition-all duration-200" style={{ width: `${progress}%` }} />
                </div>
                {progress < 100 && uploadStats && (
                  <div className="flex justify-between text-[11px] text-[#000000]" data-testid="video-upload-stats">
                    <span data-testid="video-upload-speed">{fmtSpeed(uploadStats.speed)}</span>
                    <span data-testid="video-upload-eta">{fmtEta(uploadStats.etaSec)} remaining</span>
                  </div>
                )}
              </div>
            )}
            <button type="button" className="btn-secondary self-start text-sm flex items-center gap-1" onClick={uploadFile} disabled={busy} data-testid={`${T}add-video-button`}>
              <Plus size={14} weight="bold" /> {busy ? (progress !== null ? `Uploading ${progress}%` : "Working…") : "Upload now"}
            </button>
          </>
        )}

        {tab === "link" && (
          <>
            <input data-testid={`${T}video-url-input`} className="input-base" placeholder="https://…mp4 direct link, or a YouTube / Vimeo URL" value={url} onChange={(e) => setUrl(e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input-base text-sm" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`${T}link-label-input`} />
              <select className="input-base text-sm" value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid={`${T}link-aspect-input`}>
                {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <button type="button" className="btn-secondary self-start text-sm flex items-center gap-1" onClick={addUrl} data-testid={`${T}add-link-button`}>
              <Plus size={14} weight="bold" /> Add link
            </button>
          </>
        )}

        {tab === "library" && (
          <div data-testid={`${T}video-library-picker`}>
            <div className="text-xs text-[#000000] mb-2">Reuse a video already uploaded to another lesson — no re-upload, no extra storage.</div>
            {libItems === null ? <div className="text-xs text-[#000000]">Loading…</div> : (
              <div className="grid sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
                {libItems.map((it, idx) => (
                  <button key={idx} type="button" data-testid={`${T}lib-video-${idx}`} onClick={() => addFromLibrary(it)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE] hover:border-[#CCFF00] text-left transition-all cursor-pointer">
                    <FilmStrip size={14} color="#00B8D4" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate">{it.label || it.url.split("/").pop()}</div>
                      <div className="text-[10px] text-[#000000] truncate">from “{it.from}” · {it.aspect_ratio}</div>
                    </div>
                    <Plus size={13} weight="bold" color="#CCFF00" />
                  </button>
                ))}
                {libItems.length === 0 && <div className="text-xs italic text-[#000000]">No other uploaded videos yet.</div>}
              </div>
            )}
          </div>
        )}

        {err && <div className="text-[#FF5100] text-xs">{err}</div>}
      </div>
    </div>
  );
}

function DocumentsSection({ assetId, isNew, ensureAsset, documents, setDocuments, onChanged, standalone = false, heading = null, blurb = null, testidPrefix = "" }) {
  const T = testidPrefix;
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const upload = async () => {
    setErr("");
    if (!file) { setErr("Choose a file"); return; }
    setBusy(true); setProgress(0);
    try {
      if (standalone) {
        const fd = new FormData();
        fd.append("file", file); fd.append("name", name || file.name);
        const { data } = await api.post("/admin/uploads/document", fd);
        setDocuments([...documents, data]);
      } else {
        const targetId = assetId || await ensureAsset();
        const fd = new FormData();
        fd.append("file", file); fd.append("name", name || file.name);
        await uploadWithProgress(`${API_BASE}/admin/lesson-assets/${targetId}/document`, fd, setProgress);
        await onChanged(targetId);
      }
      setName(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); setProgress(null); }
  };

  const remove = async (docId) => {
    if (standalone) { setDocuments(documents.filter(d => d.id !== docId)); return; }
    try { await api.delete(`/admin/lesson-assets/${assetId}/document/${docId}`); await onChanged(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="card mb-5" data-testid={`${T}lesson-documents-section`}>
      <h2 className="text-lg font-bold font-display flex items-center gap-1.5 mb-3"><FileText size={18} color="#CCFF00" /> {heading || "Documents / PDFs"} ({documents.length})</h2>
      <p className="text-xs text-[#000000] mb-3">{blurb || "School students must open all documents before the quiz unlocks."}</p>
      <div className="flex flex-col gap-2 mb-4">
        {documents.map(d => (
          <div key={d.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]" data-testid={`${T}lesson-doc-${d.id}`}>
            <FileText size={16} color="#52525B" />
            <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm font-semibold truncate text-[#000000] hover:text-[#00B8D4]">{d.name}</a>
            <button className="btn-ghost p-1" onClick={() => remove(d.id)} data-testid={`${T}remove-doc-${d.id}`}><Trash size={14} color="#FF5100" /></button>
          </div>
        ))}
        {documents.length === 0 && <div className="text-xs italic text-[#000000]">No documents yet.</div>}
      </div>
      <div className="p-3 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-2">
        <input className="input-base text-sm" placeholder="Document name" value={name} onChange={(e) => setName(e.target.value)} data-testid={`${T}doc-name-input`} />
        <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg" className="text-sm text-[#000000] file:bg-[#CCFF00] file:text-[#000000] file:font-bold file:rounded-full file:px-3 file:py-1.5 file:border-0 file:mr-3 file:cursor-pointer" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`${T}doc-file-input`} />
        {err && <div className="text-[#FF5100] text-xs">{err}</div>}
        {busy && progress !== null && (
          <div className="flex flex-col gap-1" data-testid={`${T}doc-upload-progress`}>
            <div className="flex justify-between text-[11px] text-[#000000]">
              <span>{progress < 100 ? "Uploading…" : "Finishing up…"}</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-[#ECEDEF] overflow-hidden">
              <div className="h-full bg-[#CCFF00] transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        <button type="button" className="btn-secondary self-start text-sm flex items-center gap-1" onClick={upload} disabled={busy} data-testid={`${T}add-doc-button`}>
          <Plus size={14} weight="bold" /> {busy ? (progress !== null ? `Uploading ${progress}%` : "Working…") : "Add document"}
        </button>
      </div>
    </div>
  );
}

// A "version" is a COMPLETE alternative lesson (its own video/docs/quiz) served to students
// matching a scope (Language / School Board / School). Authored in a slide-in side panel that
// mirrors the main lesson editor. Pre-filled by duplicating the current lesson.
function LessonVersionsSection({ swapRules, setSwapRules, langs, getCurrentLesson }) {
  const [editing, setEditing] = useState(null); // index being edited
  const [pendingNew, setPendingNew] = useState(false); // a just-added version that was never saved

  const scopeLabel = (r) => r.scope === "language"
    ? `Language: ${(langs.find(l => l.lang === r.scope_value)?.label) || r.scope_value || "—"}`
    : r.scope === "school_board" ? `Board: ${r.scope_value || "—"}` : `School: ${r.scope_value || "—"}`;

  const addVersion = () => {
    const lesson = getCurrentLesson(); // duplicate the original as a baseline
    const rule = { id: crypto.randomUUID(), scope: "language", scope_value: "", note: "", lesson };
    setSwapRules([...swapRules, rule]);
    setEditing(swapRules.length);
    setPendingNew(true);
  };
  const saveVersion = (i, rule) => { setSwapRules(swapRules.map((r, idx) => idx === i ? rule : r)); setEditing(null); setPendingNew(false); };
  const remove = (i) => setSwapRules(swapRules.filter((_, idx) => idx !== i));
  // Cancelling a brand-new version discards it — otherwise the next Save would keep a half-made rule.
  const closeEditor = () => {
    if (pendingNew && editing !== null) setSwapRules(swapRules.filter((_, idx) => idx !== editing));
    setEditing(null); setPendingNew(false);
  };

  return (
    <div className="card mb-5" data-testid="lesson-versions-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold font-display flex items-center gap-1.5"><GlobeHemisphereWest size={18} color="#00B8D4" /> Lesson versions</h2>
        <button type="button" className="btn-secondary text-sm flex items-center gap-1" onClick={addVersion} data-testid="add-lesson-version"><Plus size={14} weight="bold" /> Add version</button>
      </div>
      <p className="text-xs text-[#000000] mb-3">A version is a full alternative lesson (its own video, documents & quiz) shown to students in a specific Language, School Board, or School. Most-specific wins (School &gt; Board &gt; Language &gt; default). New versions start as a copy of this lesson.</p>
      {swapRules.length === 0 && <p className="text-sm text-[#000000] italic">No versions — every student sees the default lesson above.</p>}
      <div className="flex flex-col gap-2">
        {swapRules.map((r, i) => {
          const L = r.lesson || {};
          const nVids = (L.videos || r.videos || []).length;
          const nDocs = (L.documents || []).length;
          const hasQuiz = !!(L.quiz && (L.quiz.questions || []).length);
          return (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE]" data-testid={`lesson-version-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#000000] truncate">{L.title || "Untitled version"}</div>
                <div className="text-xs text-[#000000]">{scopeLabel(r)} · {nVids} video{nVids===1?"":"s"} · {nDocs} doc{nDocs===1?"":"s"} · {hasQuiz ? "quiz" : "no quiz"}</div>
              </div>
              <button type="button" className="btn-secondary text-xs flex items-center gap-1" style={{padding:"4px 12px"}} onClick={() => setEditing(i)} data-testid={`edit-version-${i}`}><PencilSimple size={13} /> Edit</button>
              <button type="button" className="btn-ghost p-1" onClick={() => remove(i)} data-testid={`remove-version-${i}`}><Trash size={14} color="#FF5100" /></button>
            </div>
          );
        })}
      </div>
      {editing !== null && swapRules[editing] && (
        <VersionEditorPanel
          rule={swapRules[editing]}
          langs={langs}
          conflict={(scope, value) => swapRules.some((r, idx) => idx !== editing && r.scope === scope && r.scope_value === value)}
          onSave={(rule) => saveVersion(editing, rule)}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}

const emptyQuiz = () => ({
  questions_per_attempt: 1, pass_threshold: 70,
  questions: [{ question: "", options: ["", "", "", ""], correct_index: 0, correct_answer_explanation: "" }],
});
const padQuiz = (q) => q && (q.questions || []).length ? ({
  questions_per_attempt: q.questions_per_attempt || 1,
  pass_threshold: q.pass_threshold || 70,
  questions: q.questions.map(x => ({
    id: x.id, question: x.question, options: [...x.options, "", "", "", ""].slice(0, Math.max(4, x.options.length)),
    correct_index: x.correct_index, correct_answer_explanation: x.correct_answer_explanation || "",
  })),
}) : emptyQuiz();
const normalizeQuiz = (quizOn, q) => {
  if (!quizOn) return null;
  const cleaned = q.questions
    .map(x => ({ ...x, options: x.options.filter(o => o.trim()) }))
    .filter(x => x.question.trim() && x.options.length >= 2)
    .map(x => ({ ...(x.id ? { id: x.id } : {}), question: x.question.trim(), options: x.options,
      correct_index: Math.min(x.correct_index, x.options.length - 1),
      correct_answer_explanation: (x.correct_answer_explanation || "").trim() || null }));
  if (!cleaned.length) return null;
  return { questions: cleaned, questions_per_attempt: Number(q.questions_per_attempt) || 1, pass_threshold: Number(q.pass_threshold) || 70 };
};

function VersionEditorPanel({ rule, langs, onSave, onClose, conflict = null }) {
  const L = rule.lesson || {};
  const [scope, setScope] = useState(rule.scope || "language");
  const [scopeValue, setScopeValue] = useState(rule.scope_value || "");
  const [form, setForm] = useState({
    title: L.title || "", summary: L.summary || "", content_type: L.content_type || "video",
    text_body: L.text_body || "", thumbnail_url: L.thumbnail_url || "",
    duration_minutes: L.duration_minutes || 5,
    heavy_content: !!L.heavy_content, heavy_content_description: L.heavy_content_description || "",
  });
  const [videos, setVideos] = useState(L.videos || []);
  const [documents, setDocuments] = useState(L.documents || []);
  const [quizOn, setQuizOn] = useState(!!(L.quiz && (L.quiz.questions || []).length));
  const [quiz, setQuiz] = useState(padQuiz(L.quiz));
  const [err, setErr] = useState("");

  const save = () => {
    if (!scopeValue) { setErr("Choose a scope value (language / board / school)"); return; }
    if (conflict && conflict(scope, scopeValue)) {
      setErr(scope === "language" && scopeValue === "fr"
        ? "A Français version already exists — it's managed by the French column in each section of the editor."
        : "A version with this scope already exists — edit that one instead.");
      return;
    }
    const lesson = {
      ...form,
      videos: videos.filter(v => v.url),
      documents,
      quiz: normalizeQuiz(quizOn, quiz),
    };
    onSave({ ...rule, scope, scope_value: scopeValue, lesson });
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto bg-[#E8EAED] text-[#000000]" data-testid="version-editor-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-[#000000]">Edit lesson version</SheetTitle>
          <SheetDescription className="text-[#000000]">A full alternative lesson — same building blocks as the original.</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col py-4">
          {/* Scope */}
          <div className="card mb-5">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#000000] mb-1">Show this version to</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select className="input-base text-sm w-full sm:w-44 sm:flex-none" value={scope} onChange={(e) => { setScope(e.target.value); setScopeValue(""); }} data-testid="version-scope">
                <option value="language">Language</option>
                <option value="school_board">School Board</option>
                <option value="school">School</option>
              </select>
              {scope === "language" ? (
                <select className="input-base text-sm flex-1 w-full" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} data-testid="version-scope-value">
                  <option value="">Select language…</option>
                  {langs.map(l => <option key={l.lang} value={l.lang}>{l.label || l.lang}</option>)}
                </select>
              ) : (
                <input className="input-base text-sm flex-1 w-full" placeholder={`Enter ${scope === "school_board" ? "School Board" : "School"} ID`} value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} data-testid="version-scope-value" />
              )}
            </div>
            {scope !== "language" && (
              <p className="text-xs text-[#000000] mt-2">Enter the {scope === "school_board" ? "board" : "school"} ID this version applies to. (Named {scope === "school_board" ? "boards" : "schools"} arrive in Phase 3 — for now type any identifier your test student uses.)</p>
            )}
          </div>

          {/* Basics — mirrors the original editor */}
          <div className="card mb-5 flex flex-col gap-2">
            <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">Lesson title</label>
            <input className="input-base" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="version-title" placeholder="Lesson title" />
            <textarea rows={2} className="input-base" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="version-summary" placeholder="Short summary (shown in catalog/library)" />
            <div className="flex gap-1.5">
              {[{ id: "video", label: "Video lesson", icon: VideoCamera }, { id: "text", label: "Text lesson", icon: FileText }].map(({ id: cid, label, icon: Icon }) => (
                <button key={cid} type="button" onClick={() => setForm({ ...form, content_type: cid })}
                  className="px-3 py-1.5 rounded-full text-xs font-display font-semibold flex items-center gap-1 transition-all"
                  style={{ background: form.content_type === cid ? "#CCFF00" : "#FFFFFF", color: form.content_type === cid ? "#1F2937" : "#52525B", border: "1px solid " + (form.content_type === cid ? "#CCFF00" : "#C4C8CE"), cursor: "pointer" }}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          <LessonThumbnail value={form.thumbnail_url} autoSuggested={false} onChange={(url) => setForm({ ...form, thumbnail_url: url })} />

          {form.content_type === "text" && (
            <div className="card mb-5">
              <h2 className="text-lg font-bold font-display mb-3">Lesson text</h2>
              <textarea rows={8} className="input-base" placeholder="Lesson content…" value={form.text_body} onChange={(e) => setForm({ ...form, text_body: e.target.value })} data-testid="version-text-body" />
            </div>
          )}

          {form.content_type === "video" && (
            <VideosSection standalone videos={videos} setVideos={setVideos} />
          )}

          <DocumentsSection standalone documents={documents} setDocuments={setDocuments} />

          {/* Quiz — identical to the original editor */}
          <div className="card mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold font-display flex items-center gap-1.5"><Question size={18} /> Quiz question pool</h2>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-[#000000]">
                <input type="checkbox" checked={quizOn} onChange={(e) => setQuizOn(e.target.checked)} data-testid="version-quiz-toggle" /> Attach quiz
              </label>
            </div>
            {quizOn ? <QuizEditor quiz={quiz} setQuiz={setQuiz} /> : <p className="text-sm text-[#000000] italic">No quiz on this version.</p>}
          </div>

          {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
        </div>

        <SheetFooter>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={save} data-testid="version-save">Save version</button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}


// Small per-section translate button (top-left of each bilingual section).
function TranslateBtn({ onClick, busy, done, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={!!busy}
      className="btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-[#C4C8CE] rounded-lg"
      data-testid={testid} title="AI fills this section's French column from the English — review, then Save lesson">
      {busy ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" color="#00768a" />}
      {busy ? "Traduction…" : done ? "Traduit ✓" : "Translate"}
    </button>
  );
}

// Column headers for the bilingual (English | Français) editing grid.
function ColHeads({ left = "English", right = "Français" }) {
  return (
    <div className="hidden lg:grid grid-cols-2 gap-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B]">{left}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00768a]">{right}</div>
    </div>
  );
}

function QuizEditor({ quiz, setQuiz, frQ = null, setFrQuizField = null, setFrOpt = null }) {
  const bi = !!setFrQuizField; // bilingual mode: English column | French column
  const setQ = (i, key, val) => setQuiz(p => ({ ...p, questions: p.questions.map((q, idx) => idx === i ? { ...q, [key]: val } : q) }));
  const setOpt = (i, oi, val) => setQuiz(p => ({ ...p, questions: p.questions.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) } : q) }));
  const addQ = () => setQuiz(p => ({ ...p, questions: [...p.questions, { question: "", options: ["", "", "", ""], correct_index: 0, correct_answer_explanation: "" }] }));
  const rmQ = (i) => setQuiz(p => ({ ...p, questions: p.questions.filter((_, idx) => idx !== i) }));

  return (
    <div className="flex flex-col gap-3" data-testid="quiz-editor">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs uppercase text-[#000000] flex flex-col gap-1">
          Questions served per attempt
          <input data-testid="quiz-per-attempt" type="number" min="1" className="input-base" value={quiz.questions_per_attempt} onChange={(e) => setQuiz(p => ({ ...p, questions_per_attempt: Number(e.target.value) }))} />
        </label>
        <label className="text-xs uppercase text-[#000000] flex flex-col gap-1">
          Pass threshold (%)
          <input data-testid="quiz-threshold" type="number" min="0" max="100" className="input-base" value={quiz.pass_threshold} onChange={(e) => setQuiz(p => ({ ...p, pass_threshold: Number(e.target.value) }))} />
        </label>
      </div>
      <div className="text-xs text-[#000000]">Pool of {quiz.questions.length} — app randomly serves {quiz.questions_per_attempt} per attempt.{bi ? " Empty French fields fall back to English for students." : ""}</div>
      {quiz.questions.map((q, qi) => (
        <div key={qi} className="p-3 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]" data-testid={`quiz-q-${qi}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs uppercase text-[#000000]">Q{qi + 1}</div>
            {quiz.questions.length > 1 && <button type="button" className="btn-ghost p-1" onClick={() => rmQ(qi)}><Trash size={12} color="#FF5100" /></button>}
          </div>
          {bi && <div className="mb-1.5"><ColHeads /></div>}
          <div className={bi ? "grid lg:grid-cols-2 gap-2 mb-2" : "mb-2"}>
            <input className="input-base" placeholder="Question text" value={q.question} onChange={(e) => setQ(qi, "question", e.target.value)} data-testid={`quiz-q-${qi}-text`} />
            {bi && <input className="input-base" placeholder="Question en français…" value={frQ[qi]?.question || ""} onChange={(e) => setFrQuizField(qi, { question: e.target.value })} data-testid={`fr-quiz-q-${qi}-text`} />}
          </div>
          <div className="flex flex-col gap-1 mb-2">
            {q.options.map((opt, oi) => (
              <div key={oi} className={bi ? "grid lg:grid-cols-2 gap-2" : ""}>
                <div className="flex items-center gap-1.5">
                  <input type="radio" name={`qc-${qi}`} checked={q.correct_index === oi} onChange={() => setQ(qi, "correct_index", oi)} data-testid={`quiz-q-${qi}-correct-${oi}`} />
                  <input className="input-base flex-1" placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} data-testid={`quiz-q-${qi}-opt-${oi}`} />
                </div>
                {bi && (
                  <input className="input-base" placeholder={`Option ${oi + 1} en français…`} value={(frQ[qi]?.options || [])[oi] || ""} onChange={(e) => setFrOpt(qi, oi, e.target.value)} data-testid={`fr-quiz-q-${qi}-opt-${oi}`} />
                )}
              </div>
            ))}
          </div>
          <div className={bi ? "grid lg:grid-cols-2 gap-2" : ""}>
            <input className="input-base text-sm" placeholder="Correct-answer explanation (optional — shown after a wrong answer)" value={q.correct_answer_explanation || ""} onChange={(e) => setQ(qi, "correct_answer_explanation", e.target.value)} data-testid={`quiz-q-${qi}-explanation`} />
            {bi && (
              <input className="input-base text-sm" placeholder="Explication en français…" value={frQ[qi]?.correct_answer_explanation || ""} onChange={(e) => setFrQuizField(qi, { correct_answer_explanation: e.target.value })} data-testid={`fr-quiz-q-${qi}-explanation`} />
            )}
          </div>
        </div>
      ))}
      <button type="button" className="btn-secondary self-start text-sm flex items-center gap-1" onClick={addQ} data-testid="add-quiz-question"><Plus size={14} weight="bold" /> Add question to pool</button>
    </div>
  );
}
